/**
 * The full detail of one bot (JD, every recommendation, documents, tasks,
 * activity), which the slim snapshot leaves out. Refetched when the snapshot
 * changes; a failed refetch keeps the last good detail on screen (same rule as
 * the snapshot itself: never blank, never zeros).
 */

import { useEffect, useRef, useState } from 'react';
import { BotOpsHttpError } from '../api/botOpsClient';
import { getBot } from '../api/botOpsService';
import { Bot } from '../lib/types';

export interface BotDetailState {
  detail: Bot | null;
  loading: boolean;
  error: BotOpsHttpError | null;
  notFound: boolean;
}

export function useBotDetail(slug: string, snapshotSha: string | null): BotDetailState & { reload: () => void } {
  const [state, setState] = useState<BotDetailState>({ detail: null, loading: true, error: null, notFound: false });
  const slugRef = useRef(slug);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (slugRef.current !== slug) {
      slugRef.current = slug;
      setState({ detail: null, loading: true, error: null, notFound: false });
    }
    (async () => {
      try {
        const r = await getBot(slug);
        if (!cancelled) setState({ detail: r.data, loading: false, error: null, notFound: false });
      } catch (e) {
        if (cancelled) return;
        const err = e instanceof BotOpsHttpError ? e : new BotOpsHttpError((e as Error).message || 'Unknown error', 0, 'UNKNOWN');
        setState((s) => ({ detail: s.detail, loading: false, error: err, notFound: err.status === 404 && err.code === 'BOT_OPS_BOT_NOT_FOUND' }));
      }
    })();
    return () => { cancelled = true; };
  }, [slug, snapshotSha, nonce]);

  return { ...state, reload: () => setNonce((n) => n + 1) };
}
